import { QueryClient } from '@tanstack/react-query'

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787'

// Query Client Configuration
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      retry: (failureCount, error) => {
        if (error instanceof Error && error.message.includes('404')) {
          return false
        }
        return failureCount < 3
      },
      refetchOnWindowFocus: false,
    },
  },
})

// API Error Types
export class APIError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message)
    this.name = 'APIError'
  }
}

// HTTP Client
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`
  
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new APIError(
      errorData.message || `HTTP ${response.status}: ${response.statusText}`,
      response.status,
      errorData.code
    )
  }

  return response.json()
}

// API Types
export interface Character {
  id: number
  name: string
  realm: string
  region: string
  class: string
  level: number
  avatar?: string
  guild?: {
    name: string
    realm: string
  }
}

export interface Report {
  code: string
  title: string
  startTime: number
  endTime: number
  owner: string
  zone: {
    id: number
    name: string
  }
  fights: Fight[]
}

export interface Fight {
  id: number
  name: string
  difficulty: number
  kill: boolean
  percentage: number
  startTime: number
  endTime: number
  encounterID: number
}

export interface AnalysisRequest {
  reportCode: string
  fightId: number
  characterName: string
  realm: string
  region: string
}

export interface Analysis {
  id: string
  character: Character
  fight: Fight
  dpsAnalysis: {
    overall: number
    percentile: number
    timeline: {
      timestamp: number
      dps: number
    }[]
  }
  rotationAnalysis: {
    castEfficiency: Record<string, {
      casts: number
      maxCasts: number
      efficiency: number
    }>
    downtime: number
    mistakes: {
      timestamp: number
      description: string
      severity: 'low' | 'medium' | 'high'
    }[]
  }
  recommendations: {
    category: string
    priority: 'low' | 'medium' | 'high'
    description: string
    impact: string
  }[]
  generatedAt: number
}

// API Functions
export const api = {
  // Character endpoints
  searchCharacter: async (name: string, realm: string, region: string): Promise<Character> => {
    return apiRequest<Character>(`/api/character/${encodeURIComponent(name)}/${encodeURIComponent(realm)}/${region}`)
  },

  // Report endpoints
  getReports: async (characterName: string, realm: string, region: string): Promise<Report[]> => {
    return apiRequest<Report[]>(`/api/reports?character=${encodeURIComponent(characterName)}&realm=${encodeURIComponent(realm)}&region=${region}`)
  },

  getReport: async (code: string): Promise<Report> => {
    return apiRequest<Report>(`/api/reports/${code}`)
  },

  // Analysis endpoints
  generateAnalysis: async (request: AnalysisRequest): Promise<Analysis> => {
    return apiRequest<Analysis>('/api/analysis/generate', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  },

  getAnalysis: async (id: string): Promise<Analysis> => {
    return apiRequest<Analysis>(`/api/analysis/${id}`)
  },
}

// Query Keys
export const queryKeys = {
  character: (name: string, realm: string, region: string) => 
    ['character', name, realm, region] as const,
  reports: (character: string, realm: string, region: string) => 
    ['reports', character, realm, region] as const,
  report: (code: string) => ['report', code] as const,
  analysis: (id: string) => ['analysis', id] as const,
}